import React, {useEffect, useRef, useState} from "react";
import "./Arrears.css";
import {KeyboardArrowDown, KeyboardArrowUp, Receipt, PaymentOutlined} from "@material-ui/icons";
import {motion, AnimatePresence} from "framer-motion";
import {DataGrid} from '@mui/x-data-grid';
import CryptoJS from "crypto-js";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import {ApiDetails, KEY} from "../../dummyData";

export default function Arrears(props) {
    // Decrypt tenant details from localStorage
    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);

    // State declarations
    const [arrears, setArrears] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalAmount, setTotalAmount] = useState(0);
    const hasAutoExpanded = useRef(false);

    // Effect to fetch arrears on component mount
    useEffect(() => {
        getArrears();
    }, []);

    // Calculate total arrears amount when the arrears state changes
    useEffect(() => {
        if (arrears.length > 0) {
            const total = arrears.reduce((sum, item) => sum + parseFloat(item.balance), 0);
            setTotalAmount(total);

            // Auto-expand once on first load so outstanding arrears aren't hidden behind a click
            if (!hasAutoExpanded.current) {
                hasAutoExpanded.current = true;
                setIsExpanded(true);
            }
        }
    }, [arrears]);

    // Function to fetch arrears data from API
    const getArrears = () => {
        setLoading(true);

        fetch(ApiDetails + "pegasus/visionary/tenant/get/tenant/arrears", {
            method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
            body: JSON.stringify({
                tenant_id: new Map(Object.entries(JSON.parse(originalText))).get("tenant_id"),
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                    console.log(arrears)
                    setArrears(data);
                } else {
                    props.snackBar({
                        text: "Failed fetching tenant details.",
                        type: "error",
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
                setLoading(false);
            });
    };

    // DataGrid column definitions
    const columns = [
        {
            field: "description",
            headerName: "Description",
            headerAlign: "left",
            align: "left",
            flex: 2,
            minWidth: 200,
            renderCell: (params) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 0'
                }}>
                    <Receipt style={{fontSize: 20, color: '#3A5A78'}}/>
                    <div style={{
                        fontWeight: 400,
                        color: params.row.dirty === "TRUE" ? "#991B1B" : "#3A5A78",
                        fontSize: '14px'
                    }}>
                        {params.row.description}
                    </div>
                </div>
            )
        },
        {
            field: "balance",
            headerName: "Amount",
            headerAlign: "right",
            align: "right",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={{
                    fontWeight: 600,
                    color: params.row.dirty === "TRUE" ? "#EF4444" : "#3A5A78",
                    fontSize: '14px'
                }}>
                    {parseFloat(params.row.balance).toLocaleString("en-US", {
                        style: "currency",
                        currency: "KES",
                    })}
                </div>
            )
        },
        {
            field: "date_created",
            headerName: "Date",
            headerAlign: "center",
            align: "center",
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => (
                <div style={{
                    color: '#6B7280',
                    fontSize: '13px'
                }}>
                    {params.row.date_created}
                </div>
            )
        },
        {
            field: "status",
            headerName: "Status",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                // Determine the status to display - check both payment status and dispute status
                const isPaid = params.row.status === "PAID";
                const isDisputed = params.row.dirty === "TRUE";

                let label, color, variant;

                if (isDisputed) {
                    label = "Disputed";
                    color = "#EF4444"; // Red for disputed
                    variant = "filled";
                } else if (isPaid) {
                    label = "Paid";
                    color = "#10B981"; // Green for paid
                    variant = "filled";
                } else {
                    label = "Not Paid";
                    color = "#3B82F6"; // Blue for not paid
                    variant = "outlined";
                }

                return (
                    <div style={{
                        display: 'inline-block',
                        backgroundColor: variant === "filled" ? color : 'transparent',
                        color: variant === "filled" ? 'white' : color,
                        border: variant === "outlined" ? `1px solid ${color}` : 'none',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        minWidth: '80px',
                        textAlign: 'center'
                    }}>
                        {label}
                    </div>
                );
            }
        },
    ];

    // Function to apply row styling based on dispute status
    const getRowClassName = (params) => {
        return params.row.dirty === "TRUE" ? "disputed-row" : "normal-row";
    };

    return (
        <div className="arrears-container" style={{margin: '24px 0'}}>
            {/* Loading Overlay */}
            {loading && (
                <Backdrop
                    sx={{color: '#fff', zIndex: 1000}}
                    open={loading}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
            )}

            {/* Main Card */}
            <div
                style={{
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
            >
                {/* Header with toggle button */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        backgroundColor: '#F8FAFC',
                        borderBottom: '1px solid #E5E7EB',
                        cursor: 'pointer',
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <Badge
                            badgeContent={arrears.length}
                            color="primary"
                            style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#3A5A78'
                            }}
                        >
                            <span>DETAILS OF ARREARS</span>
                        </Badge>

                        <Tooltip title="Total outstanding amount">
                            <Chip
                                label={totalAmount.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "KES",
                                })}
                                style={{
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    height: '24px',
                                    fontWeight: 600,
                                    marginLeft: '16px'
                                }}
                                size="small"
                            />
                        </Tooltip>
                    </div>

                    {isExpanded ?
                        <KeyboardArrowUp style={{color: '#3A5A78'}}/> :
                        <KeyboardArrowDown style={{color: '#3A5A78'}}/>
                    }
                </div>

                {/* Expandable Content Area */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{height: 0, opacity: 0}}
                            animate={{height: 'auto', opacity: 1}}
                            exit={{height: 0, opacity: 0}}
                            transition={{duration: 0.3}}
                            style={{overflow: 'hidden'}}
                        >
                            <div style={{
                                height: arrears.length > 0 ? 400 : 'auto',
                                width: '100%',
                                padding: '16px'
                            }}>
                                {arrears.length > 0 ? (
                                    <DataGrid
                                        rows={arrears}
                                        columns={columns}
                                        pageSize={5}
                                        rowsPerPageOptions={[5, 10, 20]}
                                        disableSelectionOnClick
                                        autoHeight
                                        getRowClassName={getRowClassName}
                                        sx={{
                                            border: 'none',
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid #f0f0f0',
                                                padding: '8px 16px',
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f5f8fa',
                                                borderRadius: '8px 8px 0 0',
                                            },
                                            '& .MuiDataGrid-virtualScroller': {
                                                backgroundColor: 'white',
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                borderTop: 'none',
                                                backgroundColor: '#f5f8fa',
                                                borderRadius: '0 0 8px 8px',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: '#f5f8fa',
                                            },
                                            '& .disputed-row': {
                                                backgroundColor: '#FEF2F2',
                                            },
                                            '& .disputed-row:hover': {
                                                backgroundColor: '#FEE2E2',
                                            },
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '200px',
                                        padding: '24px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px'
                                    }}>
                                        <PaymentOutlined style={{
                                            fontSize: '48px',
                                            color: '#3B82F6',
                                            marginBottom: '16px'
                                        }}/>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: '#3A5A78',
                                            marginBottom: '8px'
                                        }}>
                                            No Arrears Found
                                        </div>
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#6B7280',
                                            textAlign: 'center'
                                        }}>
                                            You have no outstanding arrears or payments due.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}